import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Plus } from 'lucide-react';
import rentDirectLogo from '../../assets/images/Black_RentDirect_Vertical.svg';
import rentIdLogo from '../../assets/images/rentID_Vertical.svg';
import rentShieldLogo from '../../assets/images/Black_RentShield_Verticle.svg';
import rentAiLogo from '../../assets/images/Black_RentAI_Vertical.svg';
import rentSuccessLogo from '../../assets/images/Black_RentSuccess_Vertical.svg';
import rentAcademyLogo from '../../assets/images/Black_RentAcademy_Vertical.svg';

const LEAD_FORM = 'https://api.leadconnectorhq.com/widget/form/xSNa1tu0CCXyei8UJ2ew';

const TRUST_POINTS = [
  'No contracts or commitments.',
  'No hidden fees.',
  'Upgrade or downgrade anytime.',
];

const AUDIENCES = [
  {
    label: 'Traditional Rental',
    mobileLabel: 'Traditional',
    tooltip: 'Brick-and-mortar agencies ready to digitize operations, automate workflows, and modernize their rental experience.',
  },
  {
    label: 'Marketplace Hosts',
    mobileLabel: 'Hosts',
    tooltip: 'Peer-to-peer hosts on Turo and other marketplaces looking to move to a direct rental model and keep more of their revenue.',
  },
  {
    label: 'Ride Share Rental',
    mobileLabel: 'Rideshare',
    tooltip: 'Fleet owners renting to gig drivers who need fast verification, insurance validation, and booking automation at scale.',
  },
  {
    label: 'Direct Rental',
    mobileLabel: 'Direct',
    tooltip: 'Online-first rental businesses that need a connected platform for bookings, payments, verification, and insurance in one place.',
  },
  {
    label: 'New Rental Operators',
    mobileLabel: 'New Ops',
    tooltip: 'Entrepreneurs entering the rental industry who need a turnkey platform to launch quickly and correctly from day one.',
  },
  {
    label: 'Franchise Accounts',
    mobileLabel: 'Franchise',
    tooltip: 'Larger operations with multi-location fleets who need advanced workflows, reporting, and dedicated account support.',
  },
];

const FEATURES = [
  {
    label: 'Rental Websites',
    mobileLabel: 'Websites',
    tooltip: 'Launch your own branded booking page and rent directly to customers online, in person, or through AI search — no marketplace cut.',
  },
  {
    label: 'Automated Bookings',
    mobileLabel: 'Bookings',
    tooltip: 'Smart booking automation handles scheduling, confirmations, reminders, and handoffs so you focus on running your fleet.',
  },
  {
    label: 'Trust & Safety Tools',
    mobileLabel: 'Trust',
    tooltip: 'ID verification, license checks, background screening, and MVRs run automatically at every booking to protect your business.',
  },
  {
    label: 'Insurance & Protection',
    mobileLabel: 'Insurance',
    tooltip: 'Guest protection plans, on-rent and off-rent coverage, and auto insurance validation built directly into the rental flow.',
  },
  {
    label: 'Resolution & Support',
    mobileLabel: 'Support',
    tooltip: 'Rental support agents, digital claims management, incident reporting, and a national blocklist database to resolve issues fast.',
  },
  {
    label: 'Contextual AI Agents',
    mobileLabel: 'AI Agents',
    tooltip: 'AI agents that assist at every stage — from pricing and communication to coverage recommendations — with you in control of every decision.',
  },
];

const PRODUCT_LOGOS = [
  { src: rentDirectLogo, alt: 'RentDirect', description: 'Booking & Fleet Management' },
  { src: rentIdLogo, alt: 'RentID', description: 'Trust & Safety Verification Tools' },
  { src: rentShieldLogo, alt: 'RentShield', description: 'Insurance & Protection Plans' },
  { src: rentAiLogo, alt: 'RentAI', description: 'AI Rental Agents' },
  { src: rentSuccessLogo, alt: 'RentSuccess', description: 'Resolution & Support Center' },
  { src: rentAcademyLogo, alt: 'RentAcademy', description: 'Training & Education Center' },
];

const AccordionPill = ({ label, mobileLabel, tooltip, open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className={`vipo-acc-pill${open ? ' vipo-acc-pill--open' : ''}`}
  >
    <div className="vipo-acc-pill__title-row">
      {!open && (
        <span className="vipo-acc-pill__icon" aria-hidden="true">
          <Plus size={11} strokeWidth={2.5} />
        </span>
      )}
      <span className={`vipo-acc-pill__label${open ? ' vipo-acc-pill__label--open' : ''}`}>
        <span className="vipo-acc-pill__label-mobile">{mobileLabel}</span>
        <span className="vipo-acc-pill__label-desktop">{label}</span>
      </span>
    </div>
    <div
      className="vipo-acc-pill__panel"
      style={{
        gridTemplateRows: open ? '1fr' : '0fr',
        transitionDelay: open ? '80ms' : '0ms',
      }}
    >
      <div className="vipo-acc-pill__panel-inner">
        <p className="vipo-acc-pill__tooltip">{tooltip}</p>
      </div>
    </div>
  </button>
);

// Two SEPARATE named components — each has its own useState so React never
// shares state between the audience and feature groups.
const AudiencePillGroup = () => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="vipo-acc-pill-grid">
      {AUDIENCES.map((item, i) => (
        <AccordionPill
          key={item.label}
          label={item.label}
          mobileLabel={item.mobileLabel}
          tooltip={item.tooltip}
          open={openIndex === i}
          onToggle={() => setOpenIndex((prev) => (prev === i ? null : i))}
        />
      ))}
    </div>
  );
};

const FeaturePillGroup = () => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="vipo-acc-pill-grid">
      {FEATURES.map((item, i) => (
        <AccordionPill
          key={item.label}
          label={item.label}
          mobileLabel={item.mobileLabel}
          tooltip={item.tooltip}
          open={openIndex === i}
          onToggle={() => setOpenIndex((prev) => (prev === i ? null : i))}
        />
      ))}
    </div>
  );
};

const AboutSection = () => (
  <section className="vipo-section vipo-about">
    <div className="vipo-container vipo-container--narrow">
      <p className="vipo-section__label">All in one place</p>
      <h2 className="vipo-section__heading">
        The only rental commerce platform to rent, verify, protect, manage, support and scale.
      </h2>
      <p className="vipo-section__intro vipo-about__intro">
        Everything you'll ever need to run a rental business in today's mobility economy.
      </p>

      {/* Product logos with hover descriptions */}
      <div className="vipo-about__logos">
        {PRODUCT_LOGOS.map(({ src, alt, description }) => (
          <div key={alt} className="vipo-about__logo-group">
            <img src={src} alt={alt} className="vipo-about__logo" />
            <p className="vipo-about__logo-desc">{description}</p>
          </div>
        ))}
      </div>

      {/* Two pill groups side by side with a divider between */}
      <div className="vipo-about__grid">
        <div>
          <p className="vipo-about__col-label">Who we support</p>
          <AudiencePillGroup />
        </div>

        <div className="vipo-about__divider" />

        <div>
          <p className="vipo-about__col-label">What We Do</p>
          <FeaturePillGroup />
        </div>
      </div>

      <div className="vipo-cta-row">
        <Link
          to="https://app.rentid.io/sign-up"
          className="vipo-btn vipo-btn--primary"
        >
          Get started
        </Link>
        <a
          href={LEAD_FORM}
          target="_blank"
          rel="noopener noreferrer"
          className="vipo-btn vipo-btn--secondary"
        >
          Book Demo
        </a>
      </div>

      <p className="vipo-about__tagline">
        Free to start. Only pay when rented. Subscribe and save.
      </p>

      <div className="vipo-about__trust">
        {TRUST_POINTS.map((point) => (
          <span key={point} className="vipo-about__trust-item">
            <Check className="vipo-about__trust-check" size={14} strokeWidth={2.5} />
            {point}
          </span>
        ))}
      </div>
    </div>
  </section>
);

export default AboutSection;
